import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Cookie, X } from 'lucide-react';

/**
 * GDPR / PECR compliant cookie consent banner.
 *
 * The UK PECR rule of thumb:
 *   * Strictly-necessary cookies do not require consent.
 *   * Everything else (analytics, marketing, third-party embeds) does.
 *
 * Today VoIPinger only sets strictly-necessary + Cloudflare Turnstile
 * cookies, which means a hard opt-in isn't strictly required. We still
 * show this banner because (a) the user expects to see one and (b) it
 * gives us a place to plug in analytics later without redoing the
 * consent UX.
 *
 * Decisions are persisted in localStorage under
 * `voipinger.cookie-consent` with shape
 *   { accepted_at: ISO8601, choice: 'accept' | 'reject' | 'necessary' }
 * The component is silent once a choice has been made; revisit the
 * decision by clearing the key.
 */
const STORAGE_KEY = 'voipinger.cookie-consent';

function readChoice() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && parsed.choice ? parsed : null;
  } catch (_e) {
    return null;
  }
}

function persist(choice) {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ choice, accepted_at: new Date().toISOString() }),
    );
  } catch (_e) {
    /* ignore */
  }
  // Surface the decision for any analytics provider that joins later.
  window.dispatchEvent(new CustomEvent('voipinger:consent', { detail: { choice } }));
}

export default function CookieConsent() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Defer the first paint until after hero animations so the banner
    // doesn't dominate the fold.
    const t = setTimeout(() => {
      if (!readChoice()) setVisible(true);
    }, 800);
    return () => clearTimeout(t);
  }, []);

  if (!visible) return null;

  const accept = () => { persist('accept'); setVisible(false); };
  const reject = () => { persist('reject'); setVisible(false); };
  const necessary = () => { persist('necessary'); setVisible(false); };

  return (
    <div
      role="region"
      aria-label="Cookie preferences"
      className="fixed inset-x-0 bottom-0 z-[60] px-4 pb-4 pointer-events-none"
    >
      <div className="mx-auto max-w-3xl pointer-events-auto rounded-2xl bg-white border border-slate-200 shadow-2xl shadow-slate-900/15">
        <div className="p-5 md:p-6">
          <div className="flex items-start gap-4">
            <span className="hidden md:flex w-11 h-11 rounded-xl bg-violet-50 text-violet-600 flex-shrink-0 items-center justify-center">
              <Cookie size={20} />
            </span>
            <div className="flex-1 min-w-0">
              <p className="font-bold text-slate-900 mb-1">Cookies on VoIPinger</p>
              <p className="text-sm text-slate-600 leading-relaxed">
                We use strictly-necessary cookies to keep the site working and Cloudflare Turnstile
                to block bots on our contact form. We&rsquo;d also like to set anonymised analytics
                cookies so we can learn what to improve. Choose what you&rsquo;re comfortable with.
                Read more in our <Link to="/cookies-policy" className="text-violet-700 underline hover:text-violet-900">Cookies Policy</Link>.
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={accept}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-gradient-to-r from-violet-600 to-fuchsia-500 text-white text-sm font-bold shadow-md hover:shadow-lg transition-shadow"
                >
                  Accept all
                </button>
                <button
                  type="button"
                  onClick={necessary}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-slate-100 text-slate-700 text-sm font-bold border border-slate-200 hover:bg-slate-200 transition-colors"
                >
                  Only essentials
                </button>
                <button
                  type="button"
                  onClick={reject}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-slate-500 text-sm font-semibold hover:text-slate-700 transition-colors"
                >
                  Reject non-essential
                </button>
              </div>
            </div>
            <button
              type="button"
              onClick={necessary}
              aria-label="Close cookie banner"
              className="text-slate-400 hover:text-slate-700 transition-colors flex-shrink-0"
            >
              <X size={18} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
